import { createFileRoute } from "@tanstack/react-router";
import assert from "assert";
import { ensureDefined } from "common/utils/assertOfType";
import { downloadBlob } from "common/utils/downloadFile";
import { enumKeys } from "common/utils/enum";
import type { GfxTestViewerParams } from "gfx/headless/gfxTestViewer";
import {
  defaultGfxTestViewerParams,
  initGfxTestTHREEViewer,
  parseGfxTestViewerParams,
} from "gfx/headless/gfxTestViewer";
import { designMaterialNew } from "gfx/materials/designMaterial";
import type { ThumbnailOptions } from "gfx/materials/designMaterialRenderImage";
import {
  defaultThumbnailLightOptions,
  designMaterialRenderImageOptimized,
} from "gfx/materials/designMaterialRenderImage";
import {
  ComponentViewMode,
  LayerViewMode,
  DetailRenderMode,
  BakeColorTextureMode,
} from "gfx/materials/designMaterialRuntimeConfig";
import { loadAllMaterialEditorTextures } from "gfx/materials/materialEditorTextures";
import { TestMaterialId, testDesignMaterialParams } from "gfx/materials/TestMaterial";
import type THREEViewer from "gfx/THREEViewer";
import getPageTitle from "lib/utils/getPageTitle";
import { useEffect, useRef, useState } from "react";
import { Helmet as Head } from "react-helmet-async";
import type * as THREE from "three";
import withAuthentication from "../../../components/withAuthentication";

const defaultCameraSettings = {
  name: "Default",
  fov: 20,
  ortho: false,
  x: 0,
  y: 0,
  z: 500,
  targetX: 0,
  targetY: 0,
  targetZ: 0,
  upX: 0,
  upY: 1,
  upZ: 0,
  cameraNear: 5,
  cameraFar: 1000,
};

interface MaterialRender {
  testMaterialId: TestMaterialId;
  componentViewMode: ComponentViewMode;
  bakeColorTexture: BakeColorTextureMode;
}

interface ParamsState extends GfxTestViewerParams {
  materialSettings: MaterialRender[];

  forceDetailRenderMode?: DetailRenderMode;
}

function MaterialRenderTestPage() {
  const [viewerInitialized, setViewerInitialized] = useState<boolean>(false);
  const [params, setParams] = useState<ParamsState>({
    ...defaultGfxTestViewerParams,
    materialSettings: [
      {
        testMaterialId: TestMaterialId.BRICK_TEXTURED,
        componentViewMode: ComponentViewMode.finalMaterial,
        bakeColorTexture: BakeColorTextureMode.DONT_BAKE,
      },
    ],
    forceDetailRenderMode: undefined,
  });

  const viewerRef = useRef<THREEViewer | undefined>(undefined);

  const fetchedTextures = useRef<{
    [name: string]: THREE.Texture;
  } | null>(null);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const parsedParams: ParamsState = {
      ...parseGfxTestViewerParams(urlParams),
      materialSettings: [],
      forceDetailRenderMode: undefined,
    };

    const testMaterialIds = urlParams.getAll("testMaterialId");

    const viewModes: string[] = urlParams
      .getAll("componentViewMode")
      .filter((mode) => enumKeys(ComponentViewMode).includes(mode as any)) as string[];
    if (viewModes.length === 0) {
      viewModes.push(ComponentViewMode[ComponentViewMode.finalMaterial]);
    }

    const bakeColorTexture: string =
      urlParams.get("bakeColorTexture") ?? BakeColorTextureMode[BakeColorTextureMode.DONT_BAKE];

    for (let materialIndex = 0; materialIndex < testMaterialIds.length; ++materialIndex) {
      if (!enumKeys(TestMaterialId).includes(testMaterialIds[materialIndex] as any)) {
        console.error("Invalid test material id: " + testMaterialIds[materialIndex]);
        continue;
      }

      const testMaterialId = testMaterialIds[materialIndex] as TestMaterialId;
      for (const componentViewMode of viewModes) {
        parsedParams.materialSettings.push({
          testMaterialId,
          componentViewMode: ComponentViewMode[componentViewMode],
          bakeColorTexture: BakeColorTextureMode[bakeColorTexture],
        });
      }
    }

    if (parsedParams.materialSettings.length === 0) {
      // Use textured brick material by default
      parsedParams.materialSettings.push({
        testMaterialId: TestMaterialId.BRICK_TEXTURED,
        componentViewMode: ComponentViewMode.finalMaterial,
        bakeColorTexture: BakeColorTextureMode.DONT_BAKE,
      });
    }
    const forceDetailRenderModeParam = urlParams.get("forceDetailRenderMode");
    if (forceDetailRenderModeParam !== null) {
      if (Object.keys(DetailRenderMode).includes(forceDetailRenderModeParam)) {
        parsedParams.forceDetailRenderMode = DetailRenderMode[forceDetailRenderModeParam];
      } else {
        console.error("Invalid detail render mode specified: " + forceDetailRenderModeParam);
      }
    }

    setParams(parsedParams);

    const viewer = initGfxTestTHREEViewer(parsedParams, defaultCameraSettings);

    void (async () => {
      fetchedTextures.current = await loadAllMaterialEditorTextures();
      viewerRef.current = viewer;
      setViewerInitialized(true);
    })();

    return () => {
      if (viewerRef.current) {
        const viewer = viewerRef.current;
        viewer.dispose();
        viewerRef.current = undefined;
      }
    };
  }, []);

  async function downloadThumbnail(
    testMaterialId: TestMaterialId,
    componentViewMode: ComponentViewMode,
    bakeColorTexture: BakeColorTextureMode,
  ) {
    if (!viewerRef.current || !fetchedTextures.current) {
      return;
    }
    const viewer = viewerRef.current;

    const options: ThumbnailOptions = {
      width: 256,
      height: 256,
      worldWidth: 40,
      samples: 2, // Two samples so we'll test AA, but the test is as lightweight as possible
      ...defaultThumbnailLightOptions,
    };

    const viewerRenderer = viewer.viewerRenderer;

    const materialParams = testDesignMaterialParams(testMaterialId);
    const material = await designMaterialNew(
      materialParams,
      viewerRenderer.renderer,
      (url, _overrideFormat) => {
        assert(fetchedTextures.current);
        return [fetchedTextures.current[url], Promise.resolve()];
      },
      viewer.hasSSFXRenderer,
      undefined,
      {
        forceDetailRenderMode: params.forceDetailRenderMode,
        bakeColorTexture,
        disableSDF: false,
        disableLayers: false,
        enableLightOffsetMap: false,
        offsetMapLightCount: 8,
        repeatingSDF: false,
        layerViewMode: LayerViewMode.ALL_LAYERS,
        componentViewMode,
        useWebgl2TextureAtlas: params.useWebgl2TextureAtlas,
        reproduceImplicitLodIntelBug: false,
      },
    );
    const renderParams = ensureDefined(viewerRenderer.ssfxParamsManager).getRenderParams();
    designMaterialRenderImageOptimized(
      material,
      viewerRenderer.services.imageRenderer,
      renderParams,
      options,
      (image) => {
        image.toBlob((blob) => {
          assert(blob);
          downloadBlob("material-render.png", blob);
        }, "image/png");
      },
    );
  }

  return (
    <>
      <Head>
        <title>{getPageTitle("Material Render Test")}</title>
        <meta name="robots" content="noindex" />
      </Head>
      <div>
        <div style={{ padding: "20px" }}>
          {viewerInitialized
            ? params.materialSettings.map(function (material, i) {
                return (
                  <div className="materialButton" key={material.testMaterialId + "__" + material.componentViewMode}>
                    <div>
                      {JSON.stringify(material.testMaterialId)} - {ComponentViewMode[material.componentViewMode]}
                    </div>
                    <button
                      onClick={() => {
                        const viewer = viewerRef.current;
                        if (!viewer) {
                          return;
                        }
                        void downloadThumbnail(
                          material.testMaterialId,
                          material.componentViewMode,
                          material.bakeColorTexture,
                        );
                      }}
                    >
                      Download Material Thumbnail {i}
                    </button>
                  </div>
                );
              })
            : ""}
        </div>
      </div>
    </>
  );
}

export const Route = createFileRoute("/debug/gfx-test/material-render")({
  component: withAuthentication(MaterialRenderTestPage, { superAdmin: true }),
});
